import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Mail, UserPlus, Users } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useClinicUsersList,
  usePendingInvitations,
  type ClinicMemberWithUser,
  type ClinicRole,
  type PendingInvitation,
} from '@/hooks/useClinicUsers';
import { useClinicRoles } from '@/hooks/useClinicRoles';
import { clinicRoleDisplayName, staffRoleLabel, type StaffRoleOption } from '@/lib/clinicRoleLabel';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Button, ButtonLink } from '@/components/ui/Button';
import { callFunction, ApiCallError } from '@/lib/api';
import { resolveMemberPhotoUrl } from '@/lib/memberPhoto';
import { Checkbox } from '@/components/ui/Checkbox';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { StatusChip } from '@/components/ui/Chip';
import { ResponsiveTable } from '@/components/ui/Table';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { SettingsLoading, SectionCard } from './settingsChrome';

export function ClinicUsers() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { hasPermission } = useAuth();

  const canEdit = Boolean(activeClinicId && hasPermission(activeClinicId, 'settings_users', 'update'));

  const [includeInactive, setIncludeInactive] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const {
    data: members,
    isLoading,
    error,
  } = useClinicUsersList(activeClinicId, {
    includeInactive,
  });
  const {
    data: pendingInvites,
    isLoading: pendingLoading,
    error: pendingError,
  } = usePendingInvitations(activeClinicId);
  const { data: clinicRoles, isLoading: rolesLoading } = useClinicRoles(activeClinicId);

  if (isLoading || pendingLoading || rolesLoading) {
    return <SettingsLoading />;
  }
  if (error || pendingError) return <ErrorAlert message={t('users.load_error')} />;

  const hasMembers = Boolean(members && members.length > 0);
  const hasPending = Boolean(pendingInvites && pendingInvites.length > 0);

  return (
    <div className="space-y-6">
      <SectionCard title={t('users.title')} subtitle={t('users.subtitle')} readOnly={!canEdit}>
        <div className="mb-s4 flex flex-col gap-s3 nav:flex-row nav:items-center nav:justify-between">
          <Checkbox
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
            label={t('users.show_inactive')}
          />
          {canEdit && (
            <Button type="button" onClick={() => setInviteOpen(true)} className="w-full nav:w-auto">
              <UserPlus />
              {t('users.invite_user')}
            </Button>
          )}
        </div>

        {!hasMembers && !hasPending ? (
          <EmptyState icon={<Users className="h-7 w-7" />} title={t('users.empty')} />
        ) : (
          <StaffTable
            invites={pendingInvites ?? []}
            members={members ?? []}
            clinicRoles={clinicRoles ?? []}
          />
        )}
      </SectionCard>

      {inviteOpen && activeClinicId && (
        <InviteUserModal clinicId={activeClinicId} onClose={() => setInviteOpen(false)} />
      )}
    </div>
  );
}

type StaffRow =
  | { kind: 'invite'; id: string; invite: PendingInvitation }
  | { kind: 'member'; id: string; member: ClinicMemberWithUser };

function StaffTable({
  invites,
  members,
  clinicRoles,
}: {
  invites: PendingInvitation[];
  members: ClinicMemberWithUser[];
  clinicRoles: readonly StaffRoleOption[];
}) {
  const { t } = useTranslation();
  const rows: StaffRow[] = [
    ...invites.map((invite) => ({ kind: 'invite' as const, id: invite.id, invite })),
    ...members.map((member) => ({ kind: 'member' as const, id: member.id, member })),
  ];

  return (
    <ResponsiveTable
      rows={rows}
      getRowKey={(row) => `${row.kind}-${row.id}`}
      columns={[
        {
          id: 'name',
          header: t('users.col_name'),
          cell: (row) =>
            row.kind === 'invite' ? (
              <InviteName invite={row.invite} />
            ) : (
              <MemberName member={row.member} />
            ),
        },
        {
          id: 'email',
          header: t('users.col_email'),
          hideOnMobile: true,
          cell: (row) => (
            <span className="inline-flex min-w-0 items-center gap-s2 text-ds-text">
              <Mail className="h-3.5 w-3.5 shrink-0 text-ds-muted" aria-hidden />
              <span className="min-w-0 break-all">
                {row.kind === 'invite' ? row.invite.email : (row.member.user?.email ?? '—')}
              </span>
            </span>
          ),
        },
        {
          id: 'role',
          header: t('users.col_role'),
          cell: (row) => (
            <RoleBadge
              role={row.kind === 'invite' ? row.invite.role : row.member.role}
              customRoleId={
                row.kind === 'invite' ? row.invite.custom_role_id : row.member.custom_role_id
              }
              clinicRoles={clinicRoles}
            />
          ),
        },
        {
          id: 'title',
          header: t('users.col_title'),
          hideOnMobile: true,
          cell: (row) => (row.kind === 'invite' ? '—' : (row.member.title ?? '—')),
        },
        {
          id: 'status',
          header: t('users.col_status'),
          cell: (row) =>
            row.kind === 'invite' ? (
              <InviteStatus invite={row.invite} />
            ) : (
              <StatusChip tone={row.member.status === 'active' ? 'completed' : 'neutral'}>
                {row.member.status === 'active' ? t('users.status_active') : t('users.status_inactive')}
              </StatusChip>
            ),
        },
        {
          id: 'actions',
          header: t('users.col_actions'),
          cell: (row) =>
            row.kind === 'invite' ? (
              <span className={cn(TYPE.small, 'text-ds-muted')}>
                {t('users.pending_awaiting_accept')}
              </span>
            ) : (
              <ButtonLink to={`/settings/users/${row.member.id}`} variant="link">
                {t('users.see_details')}
                <ChevronRight />
              </ButtonLink>
            ),
        },
      ]}
      mobileCard={(row) =>
        row.kind === 'invite' ? (
          <InviteCard invite={row.invite} clinicRoles={clinicRoles} />
        ) : (
          <MemberCard member={row.member} clinicRoles={clinicRoles} />
        )
      }
    />
  );
}

function InviteName({ invite }: { invite: PendingInvitation }) {
  const { t } = useTranslation();
  const name = invite.display_name?.trim() || invite.email;
  return (
    <div className="flex items-center gap-s3">
      <Avatar name={name} color="var(--amber)" photoUrl={null} />
      <div className="min-w-0">
        <div className={cn(TYPE.body, 'text-ds-text')}>{name}</div>
        <div className={cn(TYPE.small, 'text-ds-muted')}>{t('users.pending_invite_hint')}</div>
      </div>
    </div>
  );
}

function MemberName({ member }: { member: ClinicMemberWithUser }) {
  const name = member.user?.name?.trim() || member.user?.email || '—';
  return (
    <div className="flex items-center gap-s3">
      <Avatar name={name} color={member.color} photoUrl={member.photo_url} />
      <div className="min-w-0">
        <div className={cn(TYPE.body, 'text-ds-text')}>{name}</div>
        {member.user?.phone ? (
          <div className={cn(TYPE.small, 'text-ds-muted')}>{member.user.phone}</div>
        ) : null}
      </div>
    </div>
  );
}

function InviteStatus({ invite }: { invite: PendingInvitation }) {
  const { t } = useTranslation();
  const expired = new Date(invite.expires_at).getTime() < Date.now();
  return (
    <StatusChip tone={expired ? 'red' : 'draft'}>
      {expired ? t('users.status_invite_expired') : t('users.status_pending')}
    </StatusChip>
  );
}

function InviteCard({
  invite,
  clinicRoles,
}: {
  invite: PendingInvitation;
  clinicRoles: readonly StaffRoleOption[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-s2">
      <InviteName invite={invite} />
      <span className="inline-flex min-w-0 items-center gap-s2 text-ds-text">
        <Mail className="h-3.5 w-3.5 shrink-0 text-ds-muted" aria-hidden />
        <span className="min-w-0 break-all">{invite.email}</span>
      </span>
      <div className="flex flex-wrap items-center gap-s2">
        <RoleBadge
          role={invite.role}
          customRoleId={invite.custom_role_id}
          clinicRoles={clinicRoles}
        />
        <InviteStatus invite={invite} />
      </div>
      <span className={cn(TYPE.small, 'text-ds-muted')}>{t('users.pending_awaiting_accept')}</span>
    </div>
  );
}

function MemberCard({
  member,
  clinicRoles,
}: {
  member: ClinicMemberWithUser;
  clinicRoles: readonly StaffRoleOption[];
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-s2">
      <MemberName member={member} />
      <span className="inline-flex min-w-0 items-center gap-s2 text-ds-text">
        <Mail className="h-3.5 w-3.5 shrink-0 text-ds-muted" aria-hidden />
        <span className="min-w-0 break-all">{member.user?.email ?? '—'}</span>
      </span>
      {member.title ? <span className={cn(TYPE.small, 'text-ds-muted')}>{member.title}</span> : null}
      <div className="flex flex-wrap items-center gap-s2">
        <RoleBadge
          role={member.role}
          customRoleId={member.custom_role_id}
          clinicRoles={clinicRoles}
        />
        <StatusChip tone={member.status === 'active' ? 'completed' : 'neutral'}>
          {member.status === 'active' ? t('users.status_active') : t('users.status_inactive')}
        </StatusChip>
      </div>
      <ButtonLink to={`/settings/users/${member.id}`} variant="link" className="self-start">
        {t('users.see_details')}
        <ChevronRight />
      </ButtonLink>
    </div>
  );
}

export function RoleBadge({
  role,
  customRoleId = null,
  clinicRoles = [],
}: {
  role: string;
  customRoleId?: string | null;
  clinicRoles?: readonly StaffRoleOption[];
}) {
  const { t } = useTranslation();
  const tone = role === 'main_clinic_admin' || role === 'clinic_admin' ? 'upcoming' : 'neutral';
  return (
    <StatusChip tone={tone}>
      {staffRoleLabel({ role, custom_role_id: customRoleId }, clinicRoles, (key) => t(key))}
    </StatusChip>
  );
}

export function Avatar({
  name,
  color,
  photoUrl,
  size = 'md',
}: {
  name: string;
  color: string;
  photoUrl?: string | null;
  size?: 'sm' | 'md' | 'lg';
}) {
  const dim =
    size === 'lg' ? 'h-12 w-12 text-base' : size === 'sm' ? 'h-8 w-8 text-xs' : 'h-9 w-9 text-sm';
  const resolvedPhoto = resolveMemberPhotoUrl(photoUrl);
  if (resolvedPhoto) {
    return (
      <img
        src={resolvedPhoto}
        alt={name}
        className={`${dim} rounded-full object-cover ring-1 ring-inset ring-ds-line`}
      />
    );
  }
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <span
      className={`${dim} flex items-center justify-center rounded-full font-semibold uppercase text-white ring-1 ring-inset ring-ds-line`}
      style={{ backgroundColor: color }}
      aria-hidden
    >
      {initials || '?'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// InviteUserModal — calls the `invite-user` Edge Function
// ---------------------------------------------------------------------------

interface InviteUserOutput {
  invitation_id?: string;
  expires_at?: string;
  email: string;
  added_directly: boolean;
  membership_id?: string;
}

function useInviteUser(clinicId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      email: string;
      role: Extract<ClinicRole, 'clinic_admin' | 'employee'>;
      custom_role_id?: string | null;
      display_name?: string;
    }) =>
      callFunction<
        {
          clinic_id: string;
          email: string;
          role: Extract<ClinicRole, 'clinic_admin' | 'employee'>;
          custom_role_id?: string | null;
          display_name?: string;
        },
        InviteUserOutput
      >('invite-user', {
        clinic_id: clinicId,
        email: vars.email,
        role: vars.role,
        custom_role_id: vars.custom_role_id ?? null,
        display_name: vars.display_name || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['clinic-users', 'list', clinicId] });
      qc.invalidateQueries({ queryKey: ['clinic-users', 'pending-invitations', clinicId] });
    },
  });
}

/** Invite dropdown value: clinic admin, or a clinic_roles id from the matrix. */
type InviteRoleValue = 'clinic_admin' | `role:${string}`;

function InviteUserModal({ clinicId, onClose }: { clinicId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: clinicRoles, isLoading: rolesLoading } = useClinicRoles(clinicId);
  const [email, setEmail] = useState('');
  const [roleValue, setRoleValue] = useState<InviteRoleValue | ''>('');
  const [displayName, setDisplayName] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [successEmail, setSuccessEmail] = useState<string | null>(null);
  const [addedDirectly, setAddedDirectly] = useState(false);

  const { mutate: inviteUser, isPending } = useInviteUser(clinicId);

  const matrixRoles = (clinicRoles ?? []).filter((r) => r.system_key !== 'clinic_admin');

  useEffect(() => {
    if (roleValue !== '' || matrixRoles.length === 0) return;
    const employeeTemplate = matrixRoles.find((r) => r.system_key === 'employee');
    const defaultId = employeeTemplate?.id ?? matrixRoles[0].id;
    setRoleValue(`role:${defaultId}`);
  }, [matrixRoles, roleValue]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (!roleValue) {
      setFormError(t('users.invite_error_failed'));
      return;
    }

    const payload =
      roleValue === 'clinic_admin'
        ? {
            email,
            role: 'clinic_admin' as const,
            custom_role_id: null,
            display_name: displayName || undefined,
          }
        : {
            email,
            role: 'employee' as const,
            custom_role_id: roleValue.slice('role:'.length),
            display_name: displayName || undefined,
          };

    inviteUser(payload, {
      onSuccess: (data) => {
        setSuccessEmail(data.email);
        setAddedDirectly(data.added_directly);
      },
      onError: (err) => {
        if (err instanceof ApiCallError && err.code === 'conflict') {
          setFormError(t('users.invite_error_conflict'));
        } else {
          setFormError(t('users.invite_error_failed'));
        }
      },
    });
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('users.invite_modal_title')}
      footer={
        successEmail ? (
          <Button type="button" onClick={onClose} className="w-full nav:w-auto">
            {t('common.close')}
          </Button>
        ) : (
          <div className="flex w-full flex-col-reverse gap-s2 nav:w-auto nav:flex-row">
            <Button type="button" variant="tertiary" onClick={onClose} className="w-full nav:w-auto">
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="invite-user-form"
              loading={isPending}
              loadingText={t('users.invite_sending')}
              className="w-full nav:w-auto"
            >
              {t('users.invite_submit')}
            </Button>
          </div>
        )
      }
    >
      {successEmail ? (
        <p className={cn(TYPE.body, 'text-ds-text')}>
          {addedDirectly
            ? t('users.user_added_directly', { email: successEmail })
            : t('users.invite_success', { email: successEmail })}
        </p>
      ) : (
        <form id="invite-user-form" onSubmit={handleSubmit} className="flex flex-col gap-s4">
          <Input
            id="invite-email"
            type="email"
            required
            label={t('users.invite_field_email')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="off"
          />
          <Select
            id="invite-role"
            label={t('users.invite_field_role')}
            value={roleValue}
            onChange={(e) => setRoleValue(e.target.value as InviteRoleValue)}
            disabled={rolesLoading || matrixRoles.length === 0}
            hint={t('users.invite_role_hint')}
          >
            <option value="clinic_admin">{t('users.role_clinic_admin')}</option>
            {matrixRoles.map((r) => (
              <option key={r.id} value={`role:${r.id}`}>
                {clinicRoleDisplayName(r, (key) => t(key))}
                {r.is_system ? ` (${t('roles.system_badge')})` : ''}
              </option>
            ))}
          </Select>
          <Input
            id="invite-name"
            type="text"
            label={t('users.invite_field_name')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            maxLength={120}
            hint={t('users.invite_field_name_hint')}
          />
          {formError ? <ErrorAlert message={formError} /> : null}
        </form>
      )}
    </Modal>
  );
}
